import * as THREE from 'three/webgpu';

/**
 * @file Merging and flattening. The draw-call budget lives or dies here: static
 * geometry is merged per material per spatial chunk, and the result is what the
 * bake (T4) and the renderer consume.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ATTRIBUTE CONTRACT of a merged geometry
 * ─────────────────────────────────────────────────────────────────────────────
 *   position  Float32 x3  world space
 *   normal    Float32 x3  per-FACE (three identical vectors per triangle)
 *   uv        Float32 x2  metres (see prims.js) unless a caller retiled it
 *   color     Float32 x3  **ALBEDO** at this stage — the tinted base colour.
 *                         T4's bake OVERWRITES it with the day-lit value and
 *                         ADDS `color1` for night. Anything that reads `color`
 *                         before the bake is reading albedo; after, day light.
 *   color1    Float32 x3  night channel. Only exists AFTER the bake (or if a
 *                         caller merged pre-baked geometry in).
 *
 * `geo.index === null` is asserted on every output. Do not re-index.
 *
 * {@link tessellateForBake} also lives here: it is the pass that runs on the
 * merged geo map immediately BEFORE the bake, splitting quads too big to carry
 * per-vertex lighting.
 *
 * @module engine/build/mergeGeo
 */

/** Order matters: it is the order attributes are written on the output. */
const MERGE_ATTRS = /** @type {const} */ ([
  ['position', 3],
  ['normal', 3],
  ['uv', 2],
  ['color', 3],
  ['color1', 3],
]);

/**
 * @param {THREE.BufferGeometry} geo
 * @param {string} [label]
 * @returns {THREE.BufferGeometry} `geo`
 * @throws if the geometry is indexed.
 */
export function assertNonIndexed(geo, label = 'geometry') {
  if (geo.index !== null) {
    throw new Error(`${label}: expected non-indexed geometry (geo.index === null), got an index buffer`);
  }
  return geo;
}

/**
 * @param {THREE.BufferGeometry} geo
 * @returns {number}
 */
export function triCount(geo) {
  const pos = geo.getAttribute('position');
  if (!pos) return 0;
  return (geo.index ? geo.index.count : pos.count) / 3;
}

/**
 * Concatenates non-indexed geometries into ONE non-indexed geometry.
 *
 * - Indexed inputs are de-indexed on the fly (the input is not modified).
 * - `position` is required; `normal` is computed per face when an input lacks
 *   it; `uv` defaults to 0.
 * - `color` / `color1` appear on the output iff at least one input has them;
 *   inputs missing them are filled with WHITE (1,1,1) so albedo stays neutral.
 *
 * @param {Array<THREE.BufferGeometry|null|undefined>} geos
 * @returns {THREE.BufferGeometry} Always `.index === null`. Empty input yields
 *   an empty geometry (0 verts) rather than null, so callers can merge blindly.
 */
export function mergeNonIndexed(geos) {
  /** @type {Array<{geo: THREE.BufferGeometry, count: number, idx: ArrayLike<number>|null}>} */
  const parts = [];
  let total = 0;
  const present = new Set(['position', 'normal', 'uv']);

  for (const g of geos) {
    if (!g) continue;
    const pos = g.getAttribute('position');
    if (!pos || pos.count === 0) continue;
    const idx = g.index ? g.index.array : null;
    const count = idx ? idx.length : pos.count;
    if (count % 3 !== 0) {
      throw new Error('mergeNonIndexed: vertex count is not a multiple of 3 (not triangles?)');
    }
    if (g.getAttribute('color')) present.add('color');
    if (g.getAttribute('color1')) present.add('color1');
    parts.push({ geo: g, count, idx });
    total += count;
  }

  const out = new THREE.BufferGeometry();
  if (total === 0) {
    out.setAttribute('position', new THREE.BufferAttribute(new Float32Array(0), 3));
    out.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(0), 3));
    out.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(0), 2));
    return assertNonIndexed(out, 'mergeNonIndexed');
  }

  /** @type {Record<string, Float32Array>} */
  const dst = {};
  for (const [name, item] of MERGE_ATTRS) {
    if (!present.has(name)) continue;
    dst[name] = new Float32Array(total * item);
  }

  let base = 0;
  for (const part of parts) {
    const { geo, count, idx } = part;
    for (const [name, item] of MERGE_ATTRS) {
      const target = dst[name];
      if (!target) continue;
      const src = geo.getAttribute(name);
      const off = base * item;

      if (!src) {
        if (name === 'color' || name === 'color1') target.fill(1, off, off + count * item);
        else if (name === 'normal') faceNormalsInto(geo, idx, count, target, off);
        // uv stays 0
        continue;
      }
      const S = src.array;
      if (idx) {
        for (let i = 0; i < count; i++) {
          const s = idx[i] * item;
          const d = off + i * item;
          for (let k = 0; k < item; k++) target[d + k] = S[s + k];
        }
      } else {
        for (let i = 0; i < count * item; i++) target[off + i] = S[i];
      }
    }
    base += count;
  }

  for (const [name, item] of MERGE_ATTRS) {
    if (dst[name]) out.setAttribute(name, new THREE.BufferAttribute(dst[name], item));
  }
  out.computeBoundingBox();
  out.computeBoundingSphere();
  return assertNonIndexed(out, 'mergeNonIndexed');
}

/**
 * Writes per-face normals for a geometry that has none, straight into the merge
 * target (no intermediate geometry).
 * @param {THREE.BufferGeometry} geo
 * @param {ArrayLike<number>|null} idx
 * @param {number} count
 * @param {Float32Array} target
 * @param {number} off
 */
function faceNormalsInto(geo, idx, count, target, off) {
  const P = /** @type {ArrayLike<number>} */ (geo.getAttribute('position').array);
  const at = (i) => (idx ? idx[i] : i) * 3;
  for (let t = 0; t < count; t += 3) {
    const a = at(t);
    const b = at(t + 1);
    const c = at(t + 2);
    const ux = P[b] - P[a];
    const uy = P[b + 1] - P[a + 1];
    const uz = P[b + 2] - P[a + 2];
    const vx = P[c] - P[a];
    const vy = P[c + 1] - P[a + 1];
    const vz = P[c + 2] - P[a + 2];
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const l = Math.hypot(nx, ny, nz) || 1;
    nx /= l;
    ny /= l;
    nz /= l;
    for (let v = 0; v < 3; v++) {
      const d = off + (t + v) * 3;
      target[d] = nx;
      target[d + 1] = ny;
      target[d + 2] = nz;
    }
  }
}

/**
 * De-indexes (if needed) and recomputes normals so they are per-FACE. This is
 * the "make it look low-poly" button: on non-indexed geometry three's
 * `computeVertexNormals` writes the face normal to all three vertices, which is
 * exactly flat shading.
 *
 * @param {THREE.BufferGeometry} geo
 * @returns {THREE.BufferGeometry} The SAME object when the input was already
 *   non-indexed (normals rewritten in place); a NEW geometry when it had to be
 *   de-indexed — in which case the caller owns both and should dispose the
 *   original if it was a temporary.
 */
export function toFlat(geo) {
  let g = geo;
  if (g.index !== null) g = g.toNonIndexed();
  g.computeVertexNormals();
  return assertNonIndexed(g, 'toFlat');
}

/**
 * Snaps every position to a lattice of size `grid`, dropping triangles that
 * collapse to zero area, then recomputes per-face normals.
 *
 * This is the chunky-silhouette / decimation pass: `weldQuantized(geo, 0.035)`
 * on a marching-cubes creature is what turns a smooth blob into faceted low-poly
 * (plan: `quantizeWeld(0.035) -> toFlat`). On architecture, snapping to 0.25
 * kills sub-texel slivers that flicker at 52 degrees of pitch.
 *
 * @param {THREE.BufferGeometry} geo
 * @param {number} grid Lattice size in metres. `<= 0` returns a plain copy.
 * @returns {THREE.BufferGeometry} New non-indexed geometry; ALL attributes are
 *   preserved for surviving triangles. The input is untouched.
 */
export function weldQuantized(geo, grid) {
  const src = geo.index !== null ? geo.toNonIndexed() : geo;
  const pos = /** @type {THREE.BufferAttribute|undefined} */ (src.getAttribute('position'));
  if (!pos || pos.count === 0) {
    if (src !== geo) src.dispose();
    const empty = new THREE.BufferGeometry();
    empty.setAttribute('position', new THREE.BufferAttribute(new Float32Array(0), 3));
    return assertNonIndexed(empty, 'weldQuantized');
  }
  const count = pos.count;
  const attrs = Object.keys(src.attributes);

  const snap = grid > 0 ? (v) => Math.round(v / grid) * grid : (v) => v;

  // Pass 1: snap into a scratch copy and mark which triangles survive.
  const P = new Float32Array(count * 3);
  const S = pos.array;
  for (let i = 0; i < count * 3; i++) P[i] = snap(S[i]);

  /** @type {number[]} */
  const keep = [];
  for (let t = 0; t < count; t += 3) {
    const a = t * 3;
    const b = (t + 1) * 3;
    const c = (t + 2) * 3;
    const ux = P[b] - P[a];
    const uy = P[b + 1] - P[a + 1];
    const uz = P[b + 2] - P[a + 2];
    const vx = P[c] - P[a];
    const vy = P[c + 1] - P[a + 1];
    const vz = P[c + 2] - P[a + 2];
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    if (nx * nx + ny * ny + nz * nz > 1e-16) keep.push(t);
  }

  const out = new THREE.BufferGeometry();
  const n = keep.length * 3;
  for (const name of attrs) {
    const a = /** @type {THREE.BufferAttribute} */ (src.getAttribute(name));
    const item = a.itemSize;
    const target = new Float32Array(n * item);
    const source = name === 'position' ? P : a.array;
    let w = 0;
    for (const t of keep) {
      for (let v = 0; v < 3; v++) {
        const s = (t + v) * item;
        for (let k = 0; k < item; k++) target[w++] = source[s + k];
      }
    }
    out.setAttribute(name, new THREE.BufferAttribute(target, item));
  }
  if (src !== geo) src.dispose();

  if (n > 0) out.computeVertexNormals();
  out.computeBoundingBox();
  out.computeBoundingSphere();
  return assertNonIndexed(out, 'weldQuantized');
}

// ─────────────────────────────────────────────────────────────────────────────
// Pre-bake tessellation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Splits big triangles before the bake so the vertex lighting has somewhere to
 * live. EVERY level that emits a quad bigger than its AO radius needs this,
 * which is every level — run it on the merged geo map immediately before the
 * bake.
 *
 * `city/streets.js` emits a road segment as ONE QUAD and a block pavement as
 * one prism cap; `city/buildings.js` emits a storey band as one quad per edge;
 * `wasteland/highway.js` emits 9 x 3 m deck ribbons. That is the right call for
 * geometry — but the bake is PER VERTEX, so a 60 x 14 m road has exactly four
 * lighting samples, all of them at corners that happen to sit against
 * buildings. The result (verified in the browser before this existed) is whole
 * road segments reading as a single flat shade, and AO that smears across an
 * entire facade instead of pooling at its base.
 *
 * Split-longest-edge, recursively, until every edge is under `maxEdge`. It
 * refines long thin triangles along their length only, which is exactly the
 * shape a road ribbon is, and it never leaves a T-junction because both
 * triangles sharing an edge split it at the same midpoint.
 *
 * The ONE thing it must not touch is the giant fog skirt — hence `maxArea`,
 * which is per SOURCE triangle: the city skirt's halves are 405,000 m^2, the
 * boulevard's are 4,500.
 *
 * Geometries are replaced in the map (and the originals disposed) only when
 * something actually split; `userData.materialKey` is carried across.
 *
 * HISTORY: this lived in `levels/city/index.js` and, verbatim, in
 * `levels/wasteland/index.js` — both with a docblock saying it belongs here.
 * T13's audit pass moved it. Both levels now re-export it for their tests and
 * galleries.
 *
 * @param {Map<string, THREE.BufferGeometry>} geoMap Mutated in place.
 * @param {{maxEdge?: number, maxArea?: number}} [o] `maxEdge` default 3.5 m
 *   (city and wasteland both pass ~4.5); `maxArea` default 20000 m^2.
 * @returns {{before: number, after: number, ms: number}} Triangle counts.
 */
export function tessellateForBake(geoMap, o = {}) {
  const maxEdge = o.maxEdge ?? 3.5;
  const maxArea = o.maxArea ?? 20000;
  const t0 = now();
  let before = 0;
  let after = 0;

  for (const [key, geo] of geoMap) {
    const pos = geo.getAttribute('position');
    if (!pos) continue;
    before += pos.count / 3;
    const next = subdivideGeometry(geo, maxEdge, maxArea);
    after += next.getAttribute('position').count / 3;
    if (next !== geo) {
      geo.dispose();
      geoMap.set(key, next);
    }
  }
  return { before, after, ms: now() - t0 };
}

/**
 * Split-longest-edge, recursively, until every edge is under `maxEdge`. Never
 * leaves a T-junction (both triangles sharing an edge split it at the same
 * midpoint). `maxArea` is the escape hatch for the kilometre-wide haze skirt.
 * @param {THREE.BufferGeometry} geo
 * @param {number} maxEdge
 * @param {number} maxArea
 * @returns {THREE.BufferGeometry} `geo` itself when nothing needed splitting.
 */
function subdivideGeometry(geo, maxEdge, maxArea) {
  const names = Object.keys(geo.attributes);
  const attrs = names.map((n) => ({ name: n, a: geo.getAttribute(n), size: geo.getAttribute(n).itemSize }));
  const stride = attrs.reduce((s, x) => s + x.size, 0);
  const posOff = (() => {
    let o = 0;
    for (const x of attrs) {
      if (x.name === 'position') return o;
      o += x.size;
    }
    return -1;
  })();
  if (posOff < 0) return geo;

  const count = geo.getAttribute('position').count;
  const tris = count / 3;
  const lim2 = maxEdge * maxEdge;

  // Pack every vertex into one interleaved scratch row so the recursion only
  // ever has to lerp a flat array — no per-attribute bookkeeping inside the hot
  // loop.
  const src = new Float32Array(count * stride);
  {
    let o = 0;
    for (const x of attrs) {
      const A = x.a.array;
      for (let v = 0; v < count; v++) {
        for (let c = 0; c < x.size; c++) src[v * stride + o + c] = A[v * x.size + c];
      }
      o += x.size;
    }
  }

  /** @type {number[]} */
  const out = [];
  const A = new Float32Array(stride);
  const B = new Float32Array(stride);
  const C = new Float32Array(stride);
  let grew = false;

  /**
   * @param {Float32Array} a @param {Float32Array} b @param {Float32Array} c
   * @param {number} depth
   */
  const rec = (a, b, c, depth) => {
    const ax = a[posOff];
    const ay = a[posOff + 1];
    const az = a[posOff + 2];
    const bx = b[posOff];
    const by = b[posOff + 1];
    const bz = b[posOff + 2];
    const cx = c[posOff];
    const cy = c[posOff + 1];
    const cz = c[posOff + 2];
    const ab = (ax - bx) ** 2 + (ay - by) ** 2 + (az - bz) ** 2;
    const bc = (bx - cx) ** 2 + (by - cy) ** 2 + (bz - cz) ** 2;
    const ca = (cx - ax) ** 2 + (cy - ay) ** 2 + (cz - az) ** 2;
    const worst = Math.max(ab, bc, ca);

    if (depth >= 10 || worst <= lim2) {
      for (const v of [a, b, c]) for (let i = 0; i < stride; i++) out.push(v[i]);
      return;
    }
    grew = true;
    const m = new Float32Array(stride);
    if (worst === ab) {
      for (let i = 0; i < stride; i++) m[i] = (a[i] + b[i]) * 0.5;
      rec(a, m, c, depth + 1);
      rec(m, b, c, depth + 1);
    } else if (worst === bc) {
      for (let i = 0; i < stride; i++) m[i] = (b[i] + c[i]) * 0.5;
      rec(a, b, m, depth + 1);
      rec(a, m, c, depth + 1);
    } else {
      for (let i = 0; i < stride; i++) m[i] = (c[i] + a[i]) * 0.5;
      rec(a, b, m, depth + 1);
      rec(m, b, c, depth + 1);
    }
  };

  for (let t = 0; t < tris; t++) {
    for (let k = 0; k < 3; k++) {
      const dst = k === 0 ? A : k === 1 ? B : C;
      const o = (t * 3 + k) * stride;
      for (let i = 0; i < stride; i++) dst[i] = src[o + i];
    }
    // Skip anything enormous — the fog skirt, and nothing else in these levels.
    const ux = B[posOff] - A[posOff];
    const uy = B[posOff + 1] - A[posOff + 1];
    const uz = B[posOff + 2] - A[posOff + 2];
    const vx = C[posOff] - A[posOff];
    const vy = C[posOff + 1] - A[posOff + 1];
    const vz = C[posOff + 2] - A[posOff + 2];
    const area =
      0.5 * Math.hypot(uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx);
    if (area > maxArea) {
      for (const v of [A, B, C]) for (let i = 0; i < stride; i++) out.push(v[i]);
      continue;
    }
    rec(A, B, C, 0);
  }

  if (!grew) return geo;

  const n = out.length / stride;
  const next = new THREE.BufferGeometry();
  let off = 0;
  for (const x of attrs) {
    const arr = new Float32Array(n * x.size);
    for (let v = 0; v < n; v++) {
      for (let c = 0; c < x.size; c++) arr[v * x.size + c] = out[v * stride + off + c];
    }
    next.setAttribute(x.name, new THREE.BufferAttribute(arr, x.size));
    off += x.size;
  }
  next.computeBoundingBox();
  next.computeBoundingSphere();
  next.userData.materialKey = geo.userData.materialKey;
  return next;
}

/** @returns {number} */
function now() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
